import { SoapNote } from '../models/dictation.interface';

type ConsultationDisplayRecord = SoapNote & {
  subjectivePlain?: string;
  objectivePlain?: string;
  assessmentPlain?: string;
  planPlain?: string;
};

export function getConsultationDisplayText(history: SoapNote): string {
  const record = history as ConsultationDisplayRecord;
  const previewText = [
    record.subjectivePlain,
    record.objectivePlain,
    record.assessmentPlain,
    record.planPlain,
  ]
    .filter((text): text is string => !!text)
    .join('\n\n')
    .trim();

  const fallback = [history.subjective, history.objective, history.assessment, history.plan]
    .map(getPlainTextFromUnknown)
    .filter(Boolean)
    .join('\n\n')
    .trim();

  // Prefer the response markup because it retains paragraphs, breaks and list
  // structure. The plain preview fields remain a fallback for legacy RTF data.
  return formatConsultationDisplayText(fallback || previewText);
}

function getPlainTextFromUnknown(content: string | object): string {
  if (!content) {
    return '';
  }

  if (typeof content === 'string') {
    return getPlainTextFromMarkup(content);
  }

  const trustedContent = (content as { changingThisBreaksApplicationSecurity?: string })
    .changingThisBreaksApplicationSecurity;
  return typeof trustedContent === 'string' ? getPlainTextFromMarkup(trustedContent) : '';
}

function getPlainTextFromMarkup(markup: string): string {
  const div = document.createElement('div');
  div.innerHTML = markup;

  div.querySelectorAll('br').forEach((element) => element.replaceWith('\n'));
  div.querySelectorAll('li').forEach((element) => {
    element.insertBefore(document.createTextNode('- '), element.firstChild);
    element.append(document.createTextNode('\n'));
  });
  div.querySelectorAll('p, div, section, article, header, footer, h1, h2, h3, h4, h5, h6, tr')
    .forEach((element) => element.append(document.createTextNode('\n')));
  div.querySelectorAll('td, th').forEach((element) => element.append(document.createTextNode(' ')));

  return normalizeWhitespace(div.textContent ?? '');
}

function formatConsultationDisplayText(value: string): string {
  if (!value) {
    return '';
  }

  // SOAP headings arrive uppercase. Keep this case-sensitive so ordinary
  // response phrases such as "Treatment/Management plan:" stay unchanged.
  const sectionPattern = /(SUBJECTIVE|OBJECTIVE|ASSESSMENT|PLAN|VITAL SIGNS)\s*:\s*/g;
  const vitalLabelPattern = /(Alcohol Cessation(?:\s*\([^)]+\))?|Blood Pressure(?:\s*\([^)]+\))?|Body Mass Index(?:\s*\([^)]+\))?|Heart Rate(?:\s*\([^)]+\))?|Height(?:\s*\([^)]+\))?|Oxygen(?:\s*\([^)]+\))?|Respiratory Rate(?:\s*\([^)]+\))?|Smoking Cessation(?:\s*\([^)]+\))?|Temperature(?:\s*\([^)]+\))?|Weight(?:\s*\([^)]+\))?)\s*:/gi;

  const formatted = value
    .replace(sectionPattern, '\n$1:\n')
    .replace(vitalLabelPattern, '\n- $1:')
    .replace(/\s-\s+/g, '\n- ')
    .replace(/\n-\s*/g, '\n- ')
    .replace(/\n{3,}/g, '\n\n');

  return normalizeWhitespace(formatted);
}

function normalizeWhitespace(value: string): string {
  const normalizedLineEndings = value.replaceAll('\r\n', '\n').replaceAll('\r', '\n');
  let result = '';
  let pendingSpace = false;

  for (const char of normalizedLineEndings) {
    if (char === ' ' || char === '\t') {
      pendingSpace = true;
      continue;
    }
    if (char === '\n') {
      pendingSpace = false;
      result += '\n';
      continue;
    }
    if (pendingSpace && result.length > 0 && result[result.length - 1] !== '\n') {
      result += ' ';
    }
    pendingSpace = false;
    result += char;
  }

  return result.replace(/\n{3,}/g, '\n\n').trim();
}
